"use client"

import { useMemo, useState, type ReactNode } from "react"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { readQuantity } from '@/lib/tools/numeric-input'

// ---------- Calc logic ----------
// Source: US Department of Energy appliance test data — auto-defrost
// (frost-free) freezers consume roughly 40% more energy than manual-defrost
// units of equivalent capacity due to defrost-heater cycling.
// Per-cu-ft baseline (manual): 30 kWh/yr — preserved verbatim from the
// legacy calculator.

interface Results {
  autoDefrostKwh: number
  manualDefrostKwh: number
  autoDefrostCost: number
  manualDefrostCost: number
  annualDiff: number
  tenYearDiff: number
}

function computeResults(sizeNum: number | null, rate: number | null): Results | null {
  if (sizeNum === null || rate === null) return null

  const manualDefrostKwh = sizeNum * 30
  const autoDefrostKwh = manualDefrostKwh * 1.4 // DOE: ~40% more

  // Rate stored as ¢/kWh per the original input. Cost = kWh * cents / 100.
  const manualDefrostCost = (manualDefrostKwh * rate) / 100
  const autoDefrostCost = (autoDefrostKwh * rate) / 100

  const annualDiff = autoDefrostCost - manualDefrostCost
  const tenYearDiff = annualDiff * 10

  return {
    autoDefrostKwh: Math.round(autoDefrostKwh),
    manualDefrostKwh: Math.round(manualDefrostKwh),
    autoDefrostCost,
    manualDefrostCost,
    annualDiff,
    tenYearDiff,
  }
}

// ---------- Sides ----------

interface Side {
  key: "auto" | "manual"
  name: string
  tag: string
  icon: string
  color: string
  pros: string[]
  cons: string[]
}

const SIDE_AUTO: Side = {
  key: "auto",
  name: "Auto-defrost",
  tag: "The convenience pick",
  icon: "↻",
  color: "#0469f0",
  pros: [
    "Zero maintenance — defrosts itself",
    "Best for frequently-accessed freezers",
    "Standard on most uprights and built-ins",
    "Works in tighter temperature swings",
  ],
  cons: [
    "Uses ~40% more energy (per DOE)",
    "Defrost cycles cause minor temp swings",
    "Faster freezer burn on long-stored food",
    "More moving parts — more eventual failure points",
  ],
}

const SIDE_MANUAL: Side = {
  key: "manual",
  name: "Manual-defrost",
  tag: "The energy pick",
  icon: "❄",
  color: "#ff9951",
  pros: [
    "~40% less energy use (per DOE)",
    "Steadier 0°F — better for long-term storage",
    "Common on chest freezers and bulk-storage uprights",
    "Simpler mechanically — fewer failure points",
  ],
  cons: [
    "Manual defrost every 3–6 months",
    "3–4 hours of work per defrost session",
    "Frost reduces capacity if you neglect it",
    "Less convenient for daily-access freezers",
  ],
}

interface Scenario {
  who: string
  winner: "auto" | "manual"
  reason: string
}

const SCENARIOS: Scenario[] = [
  {
    who: "Bulk meat / batch-cooking household",
    winner: "manual",
    reason: "Steadier 0°F with no defrost cycles preserves quality through USDA's longest storage windows.",
  },
  {
    who: "Daily-access kitchen freezer",
    winner: "auto",
    reason: "Defrost cycling barely matters for short-term storage, and you skip the 3–4 hour quarterly chore.",
  },
  {
    who: "Garage or unconditioned space",
    winner: "manual",
    reason: "Auto-defrost sensors mis-cycle in extreme ambient temperatures. Manual handles wider swings.",
  },
  {
    who: "Outage-prone area",
    winner: "manual",
    reason: "Frost layer adds thermal mass — full manual freezers extend USDA's 48-hour outage rule meaningfully.",
  },
  {
    who: "Energy-cost focused",
    winner: "manual",
    reason: "DOE data: 40% lower kWh on equivalent capacity. The savings stack year over year.",
  },
  {
    who: "Anyone who'll skip maintenance",
    winner: "auto",
    reason: "An ignored manual freezer fills with ice and loses capacity. Auto handles it for you.",
  },
]

interface Field {
  label: string
  hint?: string
  el: ReactNode
}

function InputField({ label, hint, el }: Field) {
  return (
    <div>
      <div className="mb-2">
        <label className="block text-[12px] font-bold uppercase tracking-[0.1em] text-gray-900">
          {label}
        </label>
        {hint && <span className="block text-[10px] text-gray-500 mt-0.5">{hint}</span>}
      </div>
      {el}
    </div>
  )
}

// ---------- Page ----------

export function AutoVsManualDefrostCostPageClient() {
  const [size, setSize] = useState<string>("15")
  const [electricityRate, setElectricityRate] = useState<string>("16.5")
  const [tab, setTab] = useState<"upright" | "chest">("upright")

  const sizeNum = readQuantity(size, { min: 1, max: 50 })
  const rate = readQuantity(electricityRate, { min: 1, max: 100 })

  const results = useMemo(() => computeResults(sizeNum, rate), [sizeNum, rate])

  return (
    <ToolPageShell
      toolName="Auto vs Manual Defrost"
      heroEyebrow="Defrost"
      heroTitlePre="Auto-defrost"
      heroTitleItalic="vs"
      heroTitlePost="manual-defrost."
      heroSubtitle="Auto saves you 12–15 hours a year of maintenance. Manual saves you ~40% on energy bills, per DOE. Here's the honest cost breakdown — pick the side that wins for your situation."
      heroMeta={["⏱ ~3 min read", "DOE energy data + USDA storage guidance"]}
      heroShare={<ToolShareBar title="Auto vs Manual Defrost Cost Calculator" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-50 border border-gray-200">
          <div className="flex items-start gap-3">
            <div
              className="font-serif text-[#ff9951] leading-none flex-shrink-0"
              style={{ fontSize: 40, letterSpacing: "-0.05em" }}
            >
              &ldquo;
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-2 text-[#ff9951]">
                The short answer
              </div>
              <p
                className="font-medium text-gray-900"
                style={{
                  fontSize: 16,
                  lineHeight: 1.5,
                  letterSpacing: "-0.01em",
                  textWrap: "pretty" as never,
                }}
              >
                Pick <em className="italic text-[#ff9951] font-bold">manual-defrost</em> if you
                store bulk or long-term frozen food. Pick{" "}
                <em className="italic text-[#ff9951] font-bold">auto-defrost</em> if you open the
                freezer daily and won&apos;t keep up with maintenance.
              </p>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "How much does the 40% energy difference actually cost?",
          answer:
            "Depends on size and rate. A 15 cu ft freezer at 16.5¢/kWh (US average per EIA): manual ~$74/yr, auto ~$104/yr. Over 10 years, that's roughly $300 extra for the auto-defrost convenience.",
        },
        {
          question: "Where does the 40% number come from?",
          answer:
            "US Department of Energy appliance testing. Auto-defrost (frost-free) freezers cycle a heating element on the evaporator coils every 6–12 hours to prevent frost buildup — that defrost cycling is the source of the additional energy use.",
        },
        {
          question: "How often do I really have to defrost a manual freezer?",
          answer:
            "When frost reaches ~¼ inch thick on the walls, typically every 3–6 months in a normal household. The session takes 3–4 hours: empty into a cooler, unplug, let melt, wipe dry, refill.",
        },
        {
          question: "Which lasts longer?",
          answer:
            "Manual-defrost freezers have fewer moving parts (no defrost timer, no heating element, no evaporator fan in many models), which historically translates to longer service life. Both still typically reach 15+ years with maintenance.",
        },
        {
          question: "Does manual really keep food better?",
          answer:
            "Per USDA, food held at a steady 0°F is safe indefinitely. Steadier temperatures (no defrost-cycle thaw-refreeze) extend the quality window — texture and flavor — but not the safety window.",
        },
      ]}
      relatedTools={[
        {
          title: "Freezer Burn Prevention",
          category: "Food Storage",
          description: "USDA-grade prevention rules.",
          href: "/tools/freezer-burn-prevention-guide",
          time: "~5 min",
        },
        {
          title: "Energy Cost Calculator",
          category: "Energy & Cost",
          description: "Annual cost for any appliance.",
          href: "/tools/energy-cost-calculator",
          time: "~30 sec",
        },
        {
          title: "Freezer BTU Calculator",
          category: "Size & Capacity",
          description: "Cooling capacity for a freezer room.",
          href: "/tools/freezer-btu-calculator",
          time: "~1 min",
        },
        {
          title: "Refrigerator Lifespan Calculator",
          category: "Specialty",
          description: "Years of life remaining.",
          href: "/tools/refrigerator-lifespan-calculator",
          time: "~45 sec",
        },
      ]}
      cta={{
        title: "Ready to pick a freezer?",
        body: "Browse upright and chest freezers — auto-defrost and manual — at real prices from every major retailer.",
        buttonLabel: "Browse all freezers",
        buttonHref: "/best-freezers",
      }}
    >
      {/* ============ COST CALCULATOR ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid lg:grid-cols-[460px_1fr] gap-8 lg:gap-12">
          {/* Inputs */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-5 text-[#ff9951]">
              Compare your numbers
            </div>

            <div className="space-y-6">
              <InputField
                label="Freezer size (cu ft)"
                hint="Typical range: 5–25 cu ft"
                el={
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                  />
                }
              />

              <InputField
                label="Electricity rate (¢/kWh)"
                hint="US average is 16.5¢/kWh (EIA 2024)"
                el={
                  <input
                    type="number"
                    min={1}
                    max={100}
                    step={0.1}
                    value={electricityRate}
                    onChange={(e) => setElectricityRate(e.target.value)}
                    className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                  />
                }
              />
            </div>
          </div>

          {/* Side-by-side cost results */}
          <div>
            <div className="grid sm:grid-cols-2 gap-5 mb-6">
              {[SIDE_AUTO, SIDE_MANUAL].map((s, i) => {
                const kwh = !results
                  ? 0
                  : s.key === "auto"
                  ? results.autoDefrostKwh
                  : results.manualDefrostKwh
                const cost = !results
                  ? 0
                  : s.key === "auto"
                  ? results.autoDefrostCost
                  : results.manualDefrostCost
                return (
                  <div
                    key={s.name}
                    className="rounded-xl p-6"
                    style={{
                      border: `2px solid ${s.color}`,
                      background: i === 0 ? "rgba(4,105,240,0.03)" : "rgba(255,153,81,0.03)",
                    }}
                  >
                    <div
                      className="text-[11px] font-bold uppercase tracking-[0.15em] mb-1"
                      style={{ color: s.color }}
                    >
                      {i === 0 ? "Side A" : "Side B"}
                    </div>
                    <div className="text-[24px] font-black tracking-tight text-gray-900 mb-4">
                      {s.name}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-gray-500">
                          Annual kWh
                        </div>
                        <div
                          className="font-black"
                          style={{ fontSize: 28, letterSpacing: "-0.03em", color: s.color }}
                        >
                          {kwh}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-gray-500">
                          Annual cost
                        </div>
                        <div
                          className="font-black"
                          style={{ fontSize: 28, letterSpacing: "-0.03em", color: s.color }}
                        >
                          {`$${cost.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {results && (
              <div className="grid grid-cols-2 border-y border-gray-300 divide-x divide-gray-300 mb-6">
                <div className="py-5 pr-5">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                    Annual gap
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: 30,
                      letterSpacing: "-0.03em",
                      color: "#ff9951",
                    }}
                  >
                    {`$${results.annualDiff.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    <span className="text-[13px] font-bold text-gray-500 ml-1">/yr</span>
                  </div>
                </div>
                <div className="py-5 pl-5">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                    10-year gap
                  </div>
                  <div
                    className="font-black text-gray-900"
                    style={{ fontSize: 30, letterSpacing: "-0.03em" }}
                  >
                    {`$${Math.round(results.tenYearDiff).toLocaleString()}`}
                  </div>
                </div>
              </div>
            )}

            <p className="text-[13px] text-gray-500 leading-[1.5]">
              Manual-defrost saves the difference shown — at the cost of 12–15 hours per year of
              defrost-session labor. Pick the side that wins for your time vs energy tradeoff.
            </p>
          </div>
        </div>
      </section>

      {/* ============ SPEC FIGHT ============ */}
      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Side by side" title="The spec fight" right="6 dimensions" />

          <div className="grid md:grid-cols-2 gap-5">
            {[SIDE_AUTO, SIDE_MANUAL].map((s, i) => (
              <div
                key={s.name}
                className="rounded-xl p-6 md:p-7"
                style={{
                  border: `2px solid ${s.color}`,
                  background: i === 0 ? "rgba(4,105,240,0.03)" : "rgba(255,153,81,0.03)",
                }}
              >
                <div
                  className="flex items-center justify-between mb-4 pb-4 border-b"
                  style={{ borderColor: `${s.color}30` }}
                >
                  <div>
                    <div
                      className="text-[11px] font-bold uppercase tracking-[0.15em]"
                      style={{ color: s.color }}
                    >
                      {i === 0 ? "Side A" : "Side B"}
                    </div>
                    <div className="text-[26px] font-black tracking-tight text-gray-900">
                      {s.name}
                    </div>
                    <div className="text-[12px] text-gray-500 mt-0.5">{s.tag}</div>
                  </div>
                  <div className="text-4xl font-mono" style={{ color: s.color }}>
                    {s.icon}
                  </div>
                </div>

                <div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2"
                  style={{ borderColor: `${s.color}20` }}
                >
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-green-700 mb-2">
                      ✓ Pros
                    </div>
                    <ul className="space-y-1.5 text-[13px] text-gray-700">
                      {s.pros.map((p) => (
                        <li key={p} className="leading-[1.4]">
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500 mb-2">
                      ✕ Cons
                    </div>
                    <ul className="space-y-1.5 text-[13px] text-gray-500">
                      {s.cons.map((c) => (
                        <li key={c} className="leading-[1.4]">
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SCENARIOS ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[960px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Which wins for you?" title="Six scenarios, six verdicts" />
          <div className="border-t border-gray-900">
            {SCENARIOS.map((s, i) => (
              <div
                key={s.who}
                className="grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto] gap-3 md:gap-5 items-start md:items-center py-4 md:py-5 border-b border-gray-900"
              >
                <div className="font-mono text-[13px] font-bold min-w-[28px] text-[#ff9951] pt-1 md:pt-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="text-[15px] md:text-[16px] font-extrabold tracking-[-0.015em] text-gray-900">
                    {s.who}
                  </h3>
                  <p className="text-[13px] text-gray-600 mt-0.5 leading-[1.5]">{s.reason}</p>
                </div>
                <div className="col-start-2 md:col-auto md:text-right mt-2 md:mt-0">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-white text-[12px]"
                    style={{ background: s.winner === "auto" ? "#0469f0" : "#ff9951" }}
                  >
                    {s.winner === "auto" ? "Auto-defrost" : "Manual-defrost"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECONDARY CAROUSEL — alt style ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer fridges if budget is tight"
          description="The most affordable style to buy and to run."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ MODEL TABS ============ */}
      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Deep dive" title="Best picks per format" />
          <div className="flex gap-2 mb-6 flex-wrap">
            {(
              [
                ["upright", "Upright freezers"],
                ["chest", "Chest freezers"],
              ] as const
            ).map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setTab(v)}
                className={[
                  "px-5 py-2.5 rounded-full font-bold text-[12px] transition-all border border-gray-900",
                  tab === v ? "bg-gray-900 text-white" : "bg-white text-gray-900 hover:bg-gray-50",
                ].join(" ")}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <ToolProductCarousel
          key={tab}
          title={tab === "upright" ? "Top upright freezer picks" : "Top chest freezer picks"}
          collectionType={tab === "upright" ? "upright-freezer" : "chest-freezer"}
          description={
            tab === "upright"
              ? "Eye-level access, often auto-defrost — easier daily use."
              : "Best thermal stability, often manual-defrost — best for bulk and long-term."
          }
        />
      </section>
    </ToolPageShell>
  )
}
